import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import GeneralListItem from "@/components/general/generaListItem";
import { api } from "@/helper/api";
import { Grid, Typography } from "@mui/material";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import styles from "@/components/appointmentRequest/timeSlots.module.css";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import WorkerSlotAssignmentForm from "@/components/appointmentRequest/workerSlotAssignmentForm";
import type { BookableAppointmentSubject } from "@/components/appointmentRequest/useBookingSubjects";

interface Slot {
  id: string;
  start_date: string;
  end_date: string;
}

interface Props {
  selectedDay?: Date;
  handleShowConfirmationAfterPick: () => void;
  handleGetResponseAftetPick: (data: any) => void;
  workerBooking?: boolean;
}

export function TimeSlots(props: Props) {
  const { t } = useTranslation();
  const [slots, setSlots] = useState<Slot[]>([]);
  const [reload, setReload] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const bookingInFlight = useRef(false);
  const mounted = useRef(true);
  const { handleError, errorModal } = useErrorModal();
  const { showConfirmationModalAsync } = useConfirmationModal();
  const { appointmentrequestId = "" } = useParams();
  const selectedDay = props.selectedDay
    ? dayjs(props.selectedDay).format("YYYY-MM-DD")
    : "";

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedDay) return;
    let disposed = false;
    setSlots([]);
    setSelectedSlot(null);
    api.genericApiRequest({
      entity: "appointmentRequest",
      entityId: appointmentrequestId,
      method: "get",
      additionalRouteParts: { free_slots: "/" },
      getAllPages: true,
      parametersToRender: {
        depth: "0",
        additionalUrlParameters: {
          day: selectedDay,
          ...(props.workerBooking
            ? { include_manager_booking_context: true }
            : {}),
        },
      },
      successHandler: (res: { data: { results: Slot[] } }) => {
        if (disposed) return;
        const availableSlots = res.data.results
          .filter((slot) => !dayjs(slot.start_date).isBefore(dayjs()))
          .sort((a, b) => a.start_date.localeCompare(b.start_date));
        setSlots(availableSlots);
        if (availableSlots.length === 0)
          handleError(t("error_all_slots_taken"));
      },
      failHandler: (error: unknown) => {
        if (!disposed) handleError(error);
      },
    });
    return () => {
      disposed = true;
    };
  }, [selectedDay, appointmentrequestId, props.workerBooking, reload]);

  const slotLabel = (slot: Slot) =>
    `${dayjs(slot.start_date).format("HH:mm")} - ${dayjs(slot.end_date).format("HH:mm")}`;

  const createAppointment = async (
    slot: Slot,
    worker?: BookableAppointmentSubject,
  ) => {
    if (bookingInFlight.current) return;
    if (dayjs(slot.start_date).isBefore(dayjs())) {
      handleError(t("error_all_slots_taken"));
      return;
    }
    bookingInFlight.current = true;
    setSubmitting(true);
    const fail = (error: unknown) => {
      bookingInFlight.current = false;
      if (!mounted.current) return;
      setSubmitting(false);
      setSelectedSlot(null);
      setReload((value) => value + 1);
      handleError(error);
    };
    const success = (res: { data: unknown }) => {
      if (!mounted.current) return;
      props.handleGetResponseAftetPick(res.data);
      props.handleShowConfirmationAfterPick();
    };
    const submit = (existingAppointmentId?: string) => {
      api.genericApiRequest({
        entity: "appointment",
        method: existingAppointmentId ? "put" : "post",
        entityId: existingAppointmentId,
        additionalRouteParts: existingAppointmentId
          ? { reschedule: "/" }
          : { create_from_slot: "/" },
        parametersToRender: { depth: "1" },
        submitData: {
          slot_id: slot.id,
          ...(worker ? { impersonate_subject: worker.id } : {}),
        },
        successHandler: success,
        failHandler: fail,
      });
    };

    if (worker) {
      submit();
      return;
    }
    const confirmed = await showConfirmationModalAsync(
      t("timeslot_confirm_pick", {
        date: dayjs(slot.start_date).format("DD.MM.YYYY"),
        time: slotLabel(slot),
      }),
    );
    if (!mounted.current) return;
    if (!confirmed) {
      bookingInFlight.current = false;
      setSubmitting(false);
      return;
    }
    api.genericApiRequest({
      entity: "appointment",
      method: "get",
      getAllPages: true,
      parametersToRender: {
        depth: "1",
        additionalUrlParameters: {
          status__in:
            "open,prefilled_by_user,in_progress,review_preparation_required,waiting_for_review,post_processing_required,completed",
          datetime__gte: dayjs().startOf("day").toISOString(),
        },
      },
      successHandler: (res: {
        data: { results: { id: string; appointment_request_id: string }[] };
      }) => {
        if (!mounted.current) return;
        const existing = res.data.results.find(
          (appointment) =>
            appointment.appointment_request_id === appointmentrequestId,
        );
        submit(existing?.id);
      },
      failHandler: fail,
    });
  };

  return (
    <>
      {props.workerBooking && (
        <Typography
          component="h3"
          variant="subtitle1"
          id="worker-booking-time-label"
        >
          {t("select_appointment_time")}
        </Typography>
      )}
      <Grid
        container
        columns={6}
        columnSpacing="1rem"
        role={props.workerBooking ? "group" : undefined}
        aria-labelledby={
          props.workerBooking ? "worker-booking-time-label" : undefined
        }
      >
        {slots.map((slot) => (
          <Grid
            xs={3}
            sm={2}
            md={1.5}
            lg={1}
            item
            className={styles.gridContainer}
            key={slot.id}
            role={props.workerBooking ? "button" : undefined}
            tabIndex={props.workerBooking && !submitting ? 0 : undefined}
            aria-pressed={
              props.workerBooking ? selectedSlot?.id === slot.id : undefined
            }
            aria-disabled={submitting}
            onClick={() => {
              if (submitting) return;
              if (props.workerBooking) setSelectedSlot(slot);
              else void createAppointment(slot);
            }}
            onKeyDown={(event) => {
              if (
                props.workerBooking &&
                (event.key === "Enter" || event.key === " ")
              ) {
                event.preventDefault();
                if (!submitting) setSelectedSlot(slot);
              }
            }}
          >
            <GeneralListItem
              dataTestId={`appointment-slot-${slot.id}`}
              allowHover={!submitting}
              isDisabled={submitting}
              hideButton
              wrapperClassName={`${styles.generalListItemWrapper}${props.workerBooking && selectedSlot?.id === slot.id ? ` ${styles.selectedSlot}` : ""}`}
            >
              {slotLabel(slot)}
            </GeneralListItem>
          </Grid>
        ))}
      </Grid>
      {props.workerBooking && (
        <WorkerSlotAssignmentForm
          key={`${selectedDay}-${reload}`}
          requestId={appointmentrequestId}
          submitting={submitting}
          hasSelectedSlot={selectedSlot !== null}
          onConfirm={(subject) => {
            if (selectedSlot) void createAppointment(selectedSlot, subject);
          }}
        />
      )}
      {errorModal}
    </>
  );
}
